
import { Component, Vue, Watch } from "vue-property-decorator";
import {
  QUERY_ALL_POPULAR_DESTINATIONS,
  QUERY_SEARCH_PLACES,
  QUERY_RESOLVE_DESTINATION_CITY,
  MUTATION_CREATE_DESTINATION,
  MUTATION_UPDATE_DESTINATION,
  MUTATION_DELETE_DESTINATION,
  MUTATION_UPLOAD_PHOTO,
} from "./graphql";

interface Destination {
  id: number;
  names: Record<string, string> | null;
  googlePlaceId: string | null;
  cityId: number | null;
  islandId: number | null;
  photoUrl: string | null;
  sortOrder: number;
  active: boolean;
}

interface PlaceSuggestion {
  placeId: string;
  description: string;
  mainText: string;
  secondaryText: string;
}

@Component
export default class PopularDestinations extends Vue {
  loading = false;
  destinations: Destination[] = [];

  // Form
  formDialog = false;
  editTarget: Destination | null = null;
  formGooglePlaceId: string | null = null;
  formCityId: number | null = null;
  formIslandId: number | null = null;
  resolvedLabel: string | null = null;
  resolving = false;

  // Google Places
  placeSuggestions: PlaceSuggestion[] = [];
  placeSearch = "";
  placesLoading = false;
  selectedPlace: PlaceSuggestion | null = null;
  debounceTimer: ReturnType<typeof setTimeout> | null = null;

  // Photo
  photoDialog = false;
  photoTarget: Destination | null = null;
  photoFile: File | null = null;
  photoUploading = false;

  // Toggle
  toggleDialog = false;
  toggleTarget: Destination | null = null;

  // Delete
  deleteDialog = false;
  deleteTarget: Destination | null = null;

  get sortedDestinations(): Destination[] {
    return [...this.destinations].sort((a, b) => a.sortOrder - b.sortOrder);
  }

  async mounted() {
    await this.fetchDestinations();
  }

  @Watch("placeSearch")
  onPlaceSearchChange(val: string) {
    if (this.debounceTimer) clearTimeout(this.debounceTimer);
    if (!val || val.length < 2) {
      this.placeSuggestions = [];
      return;
    }
    this.debounceTimer = setTimeout(() => this.fetchSuggestions(val), 250);
  }

  async fetchSuggestions(input: string) {
    this.placesLoading = true;
    try {
      const { data } = await this.$apollo.query({
        query: QUERY_SEARCH_PLACES,
        variables: { input, language: "fr" },
        fetchPolicy: "no-cache"
      });
      this.placeSuggestions = data.searchPlaces || [];
    } catch {
      this.placeSuggestions = [];
    } finally {
      this.placesLoading = false;
    }
  }

  async onPlaceSelected(place: PlaceSuggestion | null) {
    if (!place) {
      this.formGooglePlaceId = null;
      this.formCityId = null;
      this.formIslandId = null;
      this.resolvedLabel = null;
      return;
    }
    this.formGooglePlaceId = place.placeId;
    this.formCityId = null;
    this.formIslandId = null;
    this.resolvedLabel = null;
    this.resolving = true;
    try {
      const { data } = await this.$apollo.query({
        query: QUERY_RESOLVE_DESTINATION_CITY,
        variables: { placeId: place.placeId },
        fetchPolicy: "no-cache"
      });
      const res = data.resolveDestinationCity;
      if (res) {
        this.formCityId = res.cityId || null;
        this.formIslandId = res.islandId || null;
        this.resolvedLabel = res.label || null;
      }
    } catch {
      // non-critical
    } finally {
      this.resolving = false;
    }
  }

  async fetchDestinations() {
    this.loading = true;
    try {
      const { data } = await this.$apollo.query({
        query: QUERY_ALL_POPULAR_DESTINATIONS,
        fetchPolicy: "no-cache"
      });
      this.destinations = data.allPopularDestinations || [];
    } catch (err) {
      this.$store.commit("setSnackMessageWithColor", {
        message: String(err),
        color: "error"
      });
    } finally {
      this.loading = false;
    }
  }

  destDisplayName(dest: Destination): string {
    if (!dest.names) return `#${dest.id}`;
    return dest.names["fr"] || dest.names[Object.keys(dest.names)[0]] || `#${dest.id}`;
  }

  openCreateDialog() {
    this.editTarget = null;
    this.formGooglePlaceId = null;
    this.formCityId = null;
    this.formIslandId = null;
    this.resolvedLabel = null;
    this.selectedPlace = null;
    this.placeSuggestions = [];
    this.placeSearch = "";
    this.formDialog = true;
  }

  openEditDialog(dest: Destination) {
    this.editTarget = dest;
    this.formGooglePlaceId = dest.googlePlaceId || null;
    this.formCityId = dest.cityId;
    this.formIslandId = dest.islandId;
    this.resolvedLabel = dest.cityId ? `Ville #${dest.cityId}` : null;
    this.selectedPlace = null;
    this.placeSuggestions = [];
    this.placeSearch = "";
    this.formDialog = true;
  }

  async saveForm() {
    try {
      if (this.editTarget) {
        await this.$apollo.mutate({
          mutation: MUTATION_UPDATE_DESTINATION,
          variables: {
            id: this.editTarget.id,
            googlePlaceId: this.formGooglePlaceId || null,
            cityId: this.formCityId,
            islandId: this.formIslandId
          }
        });
      } else {
        await this.$apollo.mutate({
          mutation: MUTATION_CREATE_DESTINATION,
          variables: {
            names: {},
            googlePlaceId: this.formGooglePlaceId || null,
            cityId: this.formCityId,
            islandId: this.formIslandId
          }
        });
      }
      this.formDialog = false;
      await this.fetchDestinations();
    } catch (err) {
      this.$store.commit("setSnackMessageWithColor", {
        message: String(err),
        color: "error"
      });
    }
  }

  confirmToggleActive(dest: Destination) {
    this.toggleTarget = dest;
    this.toggleDialog = true;
  }

  async doToggleActive() {
    if (!this.toggleTarget) return;
    try {
      await this.$apollo.mutate({
        mutation: MUTATION_UPDATE_DESTINATION,
        variables: {
          id: this.toggleTarget.id,
          active: !this.toggleTarget.active
        }
      });
      this.toggleDialog = false;
      await this.fetchDestinations();
    } catch (err) {
      this.$store.commit("setSnackMessageWithColor", {
        message: String(err),
        color: "error"
      });
    }
  }

  async moveUp(dest: Destination) {
    const sorted = this.sortedDestinations;
    const idx = sorted.findIndex(d => d.id === dest.id);
    if (idx <= 0) return;
    const prev = sorted[idx - 1];
    await Promise.all([
      this.$apollo.mutate({
        mutation: MUTATION_UPDATE_DESTINATION,
        variables: { id: dest.id, sortOrder: prev.sortOrder }
      }),
      this.$apollo.mutate({
        mutation: MUTATION_UPDATE_DESTINATION,
        variables: { id: prev.id, sortOrder: dest.sortOrder }
      })
    ]);
    await this.fetchDestinations();
  }

  async moveDown(dest: Destination) {
    const sorted = this.sortedDestinations;
    const idx = sorted.findIndex(d => d.id === dest.id);
    if (idx < 0 || idx >= sorted.length - 1) return;
    const next = sorted[idx + 1];
    await Promise.all([
      this.$apollo.mutate({
        mutation: MUTATION_UPDATE_DESTINATION,
        variables: { id: dest.id, sortOrder: next.sortOrder }
      }),
      this.$apollo.mutate({
        mutation: MUTATION_UPDATE_DESTINATION,
        variables: { id: next.id, sortOrder: dest.sortOrder }
      })
    ]);
    await this.fetchDestinations();
  }

  openPhotoUpload(dest: Destination) {
    this.photoTarget = dest;
    this.photoFile = null;
    this.photoDialog = true;
  }

  async uploadPhoto() {
    if (!this.photoFile || !this.photoTarget) return;
    this.photoUploading = true;
    try {
      await this.$apollo.mutate({
        mutation: MUTATION_UPLOAD_PHOTO,
        variables: { id: this.photoTarget.id, file: this.photoFile },
        context: { hasUpload: true }
      });
      this.$store.commit("setSnackMessageWithColor", {
        message: "Photo enregistrée",
        color: "success"
      });
      this.photoDialog = false;
      await this.fetchDestinations();
    } catch (err) {
      this.$store.commit("setSnackMessageWithColor", {
        message: String(err),
        color: "error"
      });
    } finally {
      this.photoUploading = false;
    }
  }

  confirmDelete(dest: Destination) {
    this.deleteTarget = dest;
    this.deleteDialog = true;
  }

  async doDelete() {
    if (!this.deleteTarget) return;
    try {
      await this.$apollo.mutate({
        mutation: MUTATION_DELETE_DESTINATION,
        variables: { id: this.deleteTarget.id }
      });
      this.deleteDialog = false;
      await this.fetchDestinations();
    } catch (err) {
      this.$store.commit("setSnackMessageWithColor", {
        message: String(err),
        color: "error"
      });
    }
  }
}
